import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import moment from 'moment';

import {ActionMenu} from '../action-menu/action-menu';
import {Button} from '../button/button';
import {Dialog} from '../dialog/dialog';
import {EditInPlaceCell} from '../edit-in-place/edit-in-place-cell';
import {ListViewInfiniteLoading} from '../list-view/list-view-infinite-loading';
import {StatusBadge} from '../status-badge/status-badge';
import {ResizableTable} from '../table/resizable-table';
import type {Row} from '../table/types';

import {EmailSequenceTemplateCell} from './email-sequence-template-cell';
import {EmptyState} from './empty-state';
import {ErrorState} from './error-state';
import {LeadCell} from './lead-cell';
import {SendTimeCell} from './send-time-cell';
import {TemplateFieldCell} from './template-field-cell';
import {TextButtonCell} from './text-button-cell';

import styles from './automated-email-table.module.css';

// GREP EmailAutomationField
type TemplateFieldName = 'firstName' | 'lastName' | 'companyName' | 'emailAddress';
type LeadData = {
    leadName: string;
    leadStatus: string;
    leadNumber: number | null | undefined;
    priority?: string;
    htmlUrl: string;
    avatarUrl: string;
    primaryContact: ContactData;
};
type ContactData =
    | {
          id: string;
          type: string;
          htmlUrl: string;
          name: string;
      }
    | null
    | undefined;
type TemplateFieldData = {
    materializedValue: string | null | undefined;
    required: boolean;
};
type SequenceData = {
    id: string;
    firstMessageId: string | null | undefined;
    htmlUrl: string;
    nextUnsentEmailId: string | null | undefined;
    emailSequenceTemplate:
        | {
              id: string;
              creatorUser: {
                  id: string;
              };
          }
        | null
        | undefined;
};
type SequenceTemplateData = {
    id: string;
    sequenceName: string;
    messageCount: number;
};

type EmailAutomationStatus = {
    id: string;
    status: string;
    isPaused: boolean;
    sender:
        | {
              id: string;
          }
        | null
        | undefined;
};

export type TableRowData = {
    lead: LeadData;
    templateFields: Partial<Record<TemplateFieldName, TemplateFieldData>>;
    emailSequenceTemplate: SequenceTemplateData | null | undefined;
    emailSequence: SequenceData | null | undefined;
    sendTime?: number; // unix secs,
    emailAutomationStatus: EmailAutomationStatus;
};

type OnUpdateFieldCallback = (
    id: string,
    // GREP EmailAutomationField
    fieldName: 'EMAIL_ADDRESS' | 'COMPANY_NAME' | 'FIRST_NAME' | 'LAST_NAME',
    newValue?: string | null | undefined
) => Promise<any>;

type Props = {
    data: TableRowData[];
    isLoading?: boolean;
    errMessage?: string | null | undefined;
    hasNextPage?: boolean;
    loadMore?: () => Promise<any>;
    status: 'QUEUED' | 'READY_TO_SEND' | 'NEEDS_ATTENTION';
    history: RouterHistory;
    onEmailAutomationStatusCancel?: (id: string) => Promise<any>;
    onEmailAutomationStatusStart?: (id: string) => Promise<any>;
    onUpdateField: OnUpdateFieldCallback;
    onDelayOneBusinessDay?: (arg1: string) => Promise<any>;
    onDelayOneWeek?: (arg1: string) => Promise<any>;
    onSendNow?: (arg1: string) => Promise<any>;
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

type CellProps<T> = {
    cell: {
        value: T;
        row: {
            id: number;
            values: any;
            original: TableRowData;
        };
    };
};

type DialogConfirmAction = {
    question: string;
    isWarning?: boolean;
    primaryText?: string;
    secondaryText?: string;
    onPrimary?: () => void;
    anchor: HTMLElement | null | undefined;
};

type LeadCellProps = CellProps<LeadData>;

type TemplateFieldCellProps = CellProps<TemplateFieldData> & {
    onUpdateField: OnUpdateFieldCallback;
};
type EmailSequenceTemplateCellProps = CellProps<SequenceTemplateData>;
type SendTimeCellProps = CellProps<number>;
type SendButtonCellProps = CellProps<string>;

const TEMPLATE_FIELD_TITLES = {
    firstName: 'First name',
    lastName: 'Last name',
    companyName: 'Company name',
} as const;

export function AutomatedEmailTable(props: Props) {
    const {
        status,
        onEmailAutomationStatusCancel,
        onEmailAutomationStatusStart,
        history,
        errMessage,
        isLoading,
        hasNextPage,
        onDelayOneBusinessDay,
        onDelayOneWeek,
        onSendNow,
        hasConfiguredAutomations,
        hasAutomatedEmailSequences,
    } = props;

    const data = React.useMemo(() => props.data, [props.data]);

    const columns = React.useMemo(() => {
        const leadColumn = {
            Header: 'Lead',
            accessor: 'lead',
            width: 225,
            minWidth: 150,
            maxWidth: 400,
            dataType: 'component',
            Cell: ({cell}: LeadCellProps) => (
                <div className={styles['lead-name']}>
                    <LeadCell {...cell.value} />
                </div>
            ),
        } as const;
        const emailAddressColumn = {
            Header: 'Email address',
            accessor: 'templateFields.emailAddress',
            width: 225,
            minWidth: 130,
            isEditInPlace: true,
            dataType: 'component',
            Cell: ({cell, onUpdateField}: TemplateFieldCellProps) => {
                return (
                    <EditInPlaceCell
                        name={`${cell.row.id}-emailAddress`}
                        type='email'
                        value={cell.value.materializedValue}
                        // No hover state if theres no value
                        disableDefaultInteractionStates={!cell.value.materializedValue}
                        inactiveCell={<TemplateFieldCell {...cell.value} />}
                        onChange={(newValue) =>
                            onUpdateField(
                                cell.row.original.emailAutomationStatus.id,
                                'EMAIL_ADDRESS',
                                newValue
                            )
                        }
                        placeholder='Add a email address…'
                    />
                );
            },
        } as const;

        const emailSequenceTemplateColumn = {
            Header: 'Template',
            accessor: 'emailSequenceTemplate',
            width: 225,
            minWidth: 150,
            dataType: 'component',
            Cell: ({cell}: EmailSequenceTemplateCellProps) => {
                return (
                    <div className={styles['email-sequence']}>
                        <EmailSequenceTemplateCell {...cell.value} />
                    </div>
                );
            },
        } as const;

        const firstNameTemplateColumn = {
            Header: TEMPLATE_FIELD_TITLES.firstName,
            accessor: `templateFields.firstName`,
            minWidth: 130,
            isEditInPlace: true,
            dataType: 'component',
            Cell: ({cell, onUpdateField}: TemplateFieldCellProps) => {
                return (
                    <EditInPlaceCell
                        name={`${cell.row.id}-firstName`}
                        value={cell.value.materializedValue}
                        // No hover state if the name is required and doesnt
                        // exist
                        disableDefaultInteractionStates={
                            cell.value.required && !cell.value.materializedValue
                        }
                        inactiveCell={<TemplateFieldCell {...cell.value} />}
                        onChange={(newValue) =>
                            onUpdateField(
                                cell.row.original.emailAutomationStatus.id,
                                'FIRST_NAME',
                                newValue
                            )
                        }
                        placeholder='Add a first name…'
                        {...cell.value}
                    />
                );
            },
        } as const;

        const lastNameTemplateColumn = {
            Header: TEMPLATE_FIELD_TITLES.lastName,
            accessor: `templateFields.lastName`,
            minWidth: 130,
            isEditInPlace: true,
            dataType: 'component',
            Cell: ({cell, onUpdateField}: TemplateFieldCellProps) => {
                return (
                    <EditInPlaceCell
                        name={`${cell.row.id}-lastName`}
                        value={cell.value.materializedValue}
                        inactiveCell={<TemplateFieldCell {...cell.value} />}
                        // No hover state if the name is required and doesnt
                        // exist
                        disableDefaultInteractionStates={
                            cell.value.required && !cell.value.materializedValue
                        }
                        onChange={(newValue) =>
                            onUpdateField(
                                cell.row.original.emailAutomationStatus.id,
                                'LAST_NAME',
                                newValue
                            )
                        }
                        placeholder='Add a last name…'
                        {...cell.value}
                    />
                );
            },
        } as const;

        const companyNameTemplateColumn = {
            Header: TEMPLATE_FIELD_TITLES.companyName,
            accessor: `templateFields.companyName`,
            minWidth: 130,
            isEditInPlace: true,
            dataType: 'component',
            Cell: ({cell, onUpdateField}: TemplateFieldCellProps) => {
                return (
                    <EditInPlaceCell
                        name={`${cell.row.id}-companyName`}
                        value={cell.value.materializedValue}
                        // No hover state if the company is required and doesnt
                        // exist
                        disableDefaultInteractionStates={
                            cell.value.required && !cell.value.materializedValue
                        }
                        inactiveCell={<TemplateFieldCell {...cell.value} />}
                        onChange={(newValue) =>
                            onUpdateField(
                                cell.row.original.emailAutomationStatus.id,
                                'COMPANY_NAME',
                                newValue
                            )
                        }
                        placeholder='Add a company name…'
                        {...cell.value}
                    />
                );
            },
        } as const;

        const sendTimeColumn = {
            Header: 'Scheduled to send',
            accessor: 'sendTime',
            width: 175,
            minWidth: 175,
            dataType: 'component',
            Cell: ({cell}: SendTimeCellProps) => {
                const isPaused = cell.row.original.emailAutomationStatus.isPaused;

                if (isPaused) {
                    return (
                        <StatusBadge
                            text='Paused'
                            level='warning'
                            detail={`Would normally be sent ${moment
                                .unix(cell.value)
                                .fromNow(false)}`}
                        />
                    );
                }

                return <SendTimeCell datetime={cell.value} />;
            },
        } as const;

        const StartButtonColumn = {
            Header: 'Start email',
            accessor: 'emailAutomationStatus.id',
            width: 100,
            minWidth: 100,
            dataType: 'component',
            Cell: ({cell}: SendButtonCellProps) =>
                onEmailAutomationStatusStart ? (
                    <TextButtonCell
                        text='Start'
                        onClick={() => {
                            onEmailAutomationStatusStart(cell.value);
                        }}
                    />
                ) : null,
        } as const;

        const SendNowButtonColumn = {
            Header: '',
            accessor: 'emailAutomationStatus.id',
            width: 100,
            minWidth: 100,
            dataType: 'component',
            Cell: ({cell}: SendButtonCellProps) => {
                const {emailSequence, emailAutomationStatus} = cell.row.original;

                const emailSequenceTemplate = emailSequence
                    ? emailSequence.emailSequenceTemplate
                    : null;

                const sequenceTemplateCreatorId = emailSequenceTemplate
                    ? emailSequenceTemplate.creatorUser.id
                    : null;

                const emailAutomationSenderId =
                    emailAutomationStatus && emailAutomationStatus.sender
                        ? emailAutomationStatus.sender.id
                        : null;

                // This user needs to customize their own version of this
                // automation's template in order to send their own.
                const needsCustomizedVersion =
                    sequenceTemplateCreatorId &&
                    emailAutomationSenderId &&
                    sequenceTemplateCreatorId !== emailAutomationSenderId;

                if (needsCustomizedVersion && emailSequenceTemplate) {
                    return (
                        <TextButtonCell
                            text='Customize'
                            onClick={() => {
                                history.push(
                                    `/email/templates#/templates/edit?sequenceId=${emailSequenceTemplate.id}`
                                );
                            }}
                        />
                    );
                }

                return onSendNow ? (
                    <TextButtonCell
                        text='Send now'
                        onClick={() => {
                            if (emailSequence && emailSequence.nextUnsentEmailId) {
                                onSendNow(emailSequence.nextUnsentEmailId);
                            }
                        }}
                    />
                ) : null;
            },
        } as const;

        if (status === 'NEEDS_ATTENTION') {
            return [
                leadColumn,
                emailSequenceTemplateColumn,
                firstNameTemplateColumn,
                lastNameTemplateColumn,
                companyNameTemplateColumn,
                emailAddressColumn,
            ];
        } else if (status === 'QUEUED') {
            return [
                leadColumn,
                emailSequenceTemplateColumn,
                firstNameTemplateColumn,
                lastNameTemplateColumn,
                companyNameTemplateColumn,
                sendTimeColumn,
                SendNowButtonColumn,
            ];
        }

        return [
            leadColumn,
            emailSequenceTemplateColumn,
            firstNameTemplateColumn,
            lastNameTemplateColumn,
            companyNameTemplateColumn,
            StartButtonColumn,
        ];
    }, [status, onEmailAutomationStatusStart, onSendNow, history]);

    const [confirmAction, setConfirmAction] = React.useState<
        DialogConfirmAction | null | undefined
    >();

    const renderActionMenu = React.useCallback(
        (row: Row<TableRowData>) => {
            const {
                emailSequence,
                emailAutomationStatus,
            }: {
                emailSequence: SequenceData | null | undefined;
                emailAutomationStatus: EmailAutomationStatus;
            } = row.original;

            // @ts-expect-error
            let options = [];
            const nextUnsentEmailId = emailSequence && emailSequence.nextUnsentEmailId;
            if (status === 'QUEUED' && nextUnsentEmailId) {
                if (onSendNow) {
                    // @ts-expect-error
                    options = options.concat({
                        label: 'Send now',
                        handleSelect: () => {
                            return onSendNow(nextUnsentEmailId);
                        },
                    });
                }
                if (onDelayOneBusinessDay) {
                    options = options.concat({
                        label: 'Delay 1 business day',
                        handleSelect: () => {
                            return onDelayOneBusinessDay(nextUnsentEmailId);
                        },
                    });
                }
                if (onDelayOneWeek) {
                    options = options.concat({
                        label: 'Delay 1 week',
                        handleSelect: () => {
                            return onDelayOneWeek(nextUnsentEmailId);
                        },
                    });
                }
            }

            const htmlUrl = emailSequence ? emailSequence.htmlUrl : undefined;
            if (htmlUrl) {
                options = options.concat({
                    label: 'View email',
                    handleSelect: () => {
                        history.push(htmlUrl);
                    },
                });
            }

            const firstMessageId = emailSequence ? emailSequence.firstMessageId : undefined;
            if (firstMessageId) {
                options = options.concat({
                    label: 'Edit email',
                    handleSelect: () => {
                        history.push({
                            hash: `/edit-scheduled-email/${firstMessageId}`,
                        });
                    },
                });
            }

            options = options.concat({
                label: 'Cancel email',
                variant: 'destructive',
                handleSelect: () => {
                    setConfirmAction({
                        question: 'Are you sure you want to cancel this email?',
                        isWarning: true,
                        primaryText: 'Yes, cancel email',
                        secondaryText: 'No, keep sending',
                        onPrimary: () => {
                            if (typeof onEmailAutomationStatusCancel === 'function') {
                                onEmailAutomationStatusCancel(emailAutomationStatus.id);
                            }
                        },
                        // @ts-expect-error
                        anchor: actionMenuRef.current,
                    });
                },
            });

            const actionMenuRef = React.createRef();
            const actionMenu = (
                <ActionMenu
                    key={`${emailAutomationStatus.id}-action-menu`}
                    getRef={(node) => {
                        // @ts-expect-error
                        actionMenuRef.current = node;
                    }}
                    options={options}
                />
            );

            const confirmDialog = confirmAction ? (
                <Dialog
                    key={`${emailAutomationStatus.id}-dialog`}
                    // @ts-expect-error
                    question='Are you sure?'
                    location='left'
                    onSecondary={() => {
                        // @ts-expect-error
                        setConfirmAction();
                    }}
                    {...confirmAction}
                    onPrimary={() => {
                        // @ts-expect-error
                        setConfirmAction();
                        if (typeof confirmAction.onPrimary === 'function') {
                            confirmAction.onPrimary();
                        }
                    }}
                />
            ) : null;

            return [actionMenu, confirmDialog];
        },
        [
            confirmAction,
            onEmailAutomationStatusCancel,
            status,
            history,
            onDelayOneBusinessDay,
            onDelayOneWeek,
            onSendNow,
        ]
    );

    const shouldShowTable = data.length && !errMessage;
    const shouldShowEmptyState = !isLoading && !data.length && !errMessage;

    return (
        <React.Fragment>
            {shouldShowTable ? (
                <div className={styles['automated-email-table']}>
                    <ResizableTable
                        isCompact={true}
                        hasHeaderBorder={true}
                        hasHeaderCellBorders={true}
                        stickyHeaders={true}
                        disableFilters={true}
                        disableSorting={true}
                        columns={columns}
                        data={data}
                        // @ts-expect-error
                        renderRowActionMenu={renderActionMenu}
                        customCellFunctions={{
                            onUpdateField: props.onUpdateField,
                        }}
                    />
                </div>
            ) : undefined}
            {hasNextPage && !isLoading ? (
                <Button
                    variant='text-primary'
                    size='big'
                    disabled={isLoading}
                    onClick={props.loadMore}
                >
                    Load more rows
                </Button>
            ) : null}
            {isLoading ? <ListViewInfiniteLoading /> : null}
            {shouldShowEmptyState ? (
                <EmptyState
                    hasAutomatedEmailSequences={hasAutomatedEmailSequences}
                    hasConfiguredAutomations={hasConfiguredAutomations}
                    status={status}
                />
            ) : null}
            {!isLoading && !data.length && errMessage ? <ErrorState message={errMessage} /> : null}
        </React.Fragment>
    );
}
